import React, { type PropsWithChildren, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import useScrollbarSize from 'react-scrollbar-size'
import { Box, type Theme } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import classNames from 'classnames'

import RightRail from '~/components/common/RightRail'
import FeatureToggle from '~/constants/featureToggle'
import { RxRequestPrimaryFilters } from '~/constants/taskConstants'
import { fetchTask } from '~/store/actions/tasks'
import { updateTimelineFilters } from '~/store/actions/timeline'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getPatient } from '~/store/reducers/patients'
import { getTask, getTasksIsFetching } from '~/store/reducers/tasks'

import DetailsBackButton from '../../clients/DetailsBackButton'
import TimelineComponent from '../../clients/timeline/TimelineComponent'
import TopMenu from '../../header/top-menu/TopMenu'
import { RightRailOverlayProvider } from '../../soapV2/context/RightRailOverlayContext'
import PatientDetailsHeader from '../../soapV2/summary/patient-details/PatientDetailsHeader'
import { PatientMedicalSummaryRail } from '../../soapV2/summary/PatientMedicalSummaryRail'
import RxRequestPrescription from './RxRequestPrescription'

import rightRailStyles from './RxRequestTaskDetails.module.scss'

const FROM_TIMELINE = 'timeline'

interface LocationState {
  from?: string
  returnPath?: string
}

/** Reads return context from query (?from=timeline&returnPath=...) or location.state. Uses stable id 'timeline', not locale-dependent labels. */
function getReturnContext(location: { search: string; state: unknown }): {
  fromTimeline: boolean
  returnPath: string | null
} {
  const params = new URLSearchParams(location.search)
  const returnPathFromQuery = params.get('returnPath')?.trim() || null
  const fromTimelineFromQuery =
    params.get('from') === FROM_TIMELINE && returnPathFromQuery
  const state = location.state as LocationState | null
  const fromTimelineFromState =
    state?.from === FROM_TIMELINE && Boolean(state?.returnPath)
  return {
    fromTimeline: Boolean(fromTimelineFromQuery || fromTimelineFromState),
    returnPath: (returnPathFromQuery || state?.returnPath) ?? null,
  }
}

interface StyleProps {
  scrollbarWidth: number
}

const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  task: {
    height: `calc(100vh - ${
      (theme.mixins.toolbar.minHeight as number) +
      theme.constants.progressBarHeight
    }px)`,
    overflowY: 'auto',
    overflowX: 'hidden',
    top: theme.mixins.toolbar.minHeight,
    WebkitOverflowScrolling: 'touch',
    zIndex: theme.utils.modifyZIndex(theme.zIndex.rightRail, 'below'),
    position: 'fixed',
    [theme.breakpoints.down('md')]: {
      top: 0,
      paddingTop:
        (theme.mixins.toolbar as any)[theme.breakpoints.down('md')].minHeight +
        Number.parseInt(theme.spacing(1), 10),
      height: '100%',
    },
  },
  rail: {
    paddingRight: theme.spacing(2),
  },
  awoRail: {
    boxShadow: 'none',
  },
  halfScreenWidth: {
    width: ({ scrollbarWidth }: StyleProps) =>
      `calc(50vw - ${theme.constants.leftMenuWidth + scrollbarWidth}px / 2)`,
    [theme.breakpoints.down('md')]: {
      width: ({ scrollbarWidth }: StyleProps) =>
        `calc(50vw - ${scrollbarWidth}px / 2)`,
    },
  },
}))

export interface RxRequestTaskDetailsProps extends PropsWithChildren {
  itemId: string
  onClose: () => void
}

const RxRequestTaskDetails = ({
  itemId,
  onClose,
}: RxRequestTaskDetailsProps) => {
  const { t } = useTranslation(['Tasks', 'Clients'])

  const { width: scrollbarWidth } = useScrollbarSize()

  const classes = useStyles({ scrollbarWidth })

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const task = useSelector(getTask(itemId))
  const tasksIsFetching = useSelector(getTasksIsFetching)
  const isRxTrackerPhase1Enabled = useSelector(
    getFeatureToggle(FeatureToggle.RX_TRACKER_PHASE_1),
  )

  const clientId = task?.client
  const patientId = task?.patient
  const patient = useSelector(getPatient(patientId))

  const { fromTimeline, returnPath } = getReturnContext(location)
  const showRecordsLabel =
    isRxTrackerPhase1Enabled && fromTimeline && returnPath

  useEffect(() => {
    if (itemId) {
      dispatch(fetchTask(itemId))
    }
  }, [itemId, dispatch])

  useEffect(
    () => () => {
      dispatch(updateTimelineFilters([]))
    },
    [],
  )

  const showRightRail = clientId && patientId
  const showAWORightRail = isRxTrackerPhase1Enabled && showRightRail
  const showTimelineRightRail = !isRxTrackerPhase1Enabled && showRightRail

  return (
    <>
      <Box
        className={
          showRightRail && classNames(classes.task, classes.halfScreenWidth)
        }
      >
        <TopMenu>
          <DetailsBackButton
            fullWidth
            thin
            onClick={() => {
              if (!isRxTrackerPhase1Enabled) {
                onClose()
                return
              }
              const ctx = getReturnContext(location)
              if (ctx.fromTimeline && ctx.returnPath) {
                navigate(ctx.returnPath)
              } else {
                onClose()
              }
            }}
          >
            {showRecordsLabel
              ? t('Clients:SNAPSHOTS_AND_RECORDS.PATIENT_RECORDS_LABEL')
              : t('Tasks:TASKS_LIST')}
          </DetailsBackButton>
        </TopMenu>
        <Box pt={1}>
          <RxRequestPrescription
            task={task}
            tasksIsFetching={tasksIsFetching}
            onClose={onClose}
          />
        </Box>
      </Box>
      {showAWORightRail && (
        <RightRail
          visible
          className={classNames(
            classes.rail,
            classes.awoRail,
            classes.halfScreenWidth,
          )}
        >
          <RightRailOverlayProvider>
            <div className={rightRailStyles.rightRailContainer}>
              <div className={rightRailStyles.patientDetailsHeader}>
                <PatientDetailsHeader clientId={clientId} patient={patient} />
              </div>
              <PatientMedicalSummaryRail
                clientId={clientId}
                patientId={patientId}
              />
            </div>
          </RightRailOverlayProvider>
        </RightRail>
      )}
      {showTimelineRightRail && (
        <RightRail
          visible
          className={classNames(classes.rail, classes.halfScreenWidth)}
        >
          <TimelineComponent
            filtersAlwaysOnTop
            clientId={clientId}
            filterProps={{
              filtersSelectedByDefault: RxRequestPrimaryFilters,
              collapseConfig: {
                primaryFilters: RxRequestPrimaryFilters,
                isExpandedByDefault: false,
              },
            }}
            patientId={patientId}
            useWindowScroll={false}
          />
        </RightRail>
      )}
    </>
  )
}

export default RxRequestTaskDetails
